import { useCallback } from 'react';

import { DeliveryMethod } from 'apps/shared/scripts/__generated__/graphql.generated';
import { useDeliverCard } from 'apps/shared/scripts/graphql';
import { useFormCallback, useToast } from 'apps/shared/scripts/hooks';

import { CardDeliveryForm } from '../../components';
import { CardDeliveryFormValues } from '../../components/CardDeliveryForm';

interface Props {
  cardId: string;
}

const CardDeliveryFormContainer = ({ cardId }: Props) => {
  const { mutate: deliverCard, isMutating: isDelivering } = useDeliverCard({
    refetchQueries: ['Card'],
    awaitRefetchQueries: true,
  });
  const { isSuccess, onSuccess, onError } = useFormCallback();
  const toast = useToast();

  const onSubmit = useCallback(
    async (data: CardDeliveryFormValues) => {
      const { recipientEmail, deliveryMethod } = data;
      const isManual = deliveryMethod === DeliveryMethod.Manual;
      try {
        const { result, errors } = await deliverCard({
          id: cardId,
          input: isManual
            ? { method: DeliveryMethod.Manual }
            : {
                email: recipientEmail,
                method: DeliveryMethod.Email,
              },
        });
        if (!result || errors.length > 0) {
          onError(errors[0]);
        } else {
          onSuccess({
            message: `Success! Card was successfully ${isManual ? 'published' : 'sent'}`,
          });
        }
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message);
        }
      }
    },
    [cardId, deliverCard, onError, onSuccess, toast],
  );

  return (
    <CardDeliveryForm
      cardId={cardId}
      isSubmitting={isDelivering}
      isSuccess={isSuccess}
      onSubmit={onSubmit}
    />
  );
};

CardDeliveryFormContainer.displayName = 'CardDeliveryFormContainer';

export default CardDeliveryFormContainer;
